import ExcelJS from 'exceljs'

/** Same tab names as `projectAndRoomSchema` / NewProjectImport. */
export const DLSTRACKER_PROJECT_TAB = 'DLSTracker_Project'
export const DLSTRACKER_SERVICES_TAB = 'DLSTracker_Services'

/** Normalize project # strings so Excel en-dashes / spaced hyphens match app `companyProjectNumber`. */
export function normalizeCompanyProjectNumberToken(raw) {
  return String(raw ?? '')
    .trim()
    .replace(/[\u2013\u2014\u2212]/g, '-') // en dash, em dash, minus sign → ASCII hyphen
    .replace(/\s*-\s*/g, '-') // " - " → "-"
}

/**
 * Derive numeric change order # from the estimate's companyProjectNumber vs parent base.
 * Supports `BASE-02` / `BASE-2` (dash) or concatenated `BASE02` / `BASE2` (legacy new-CO style).
 */
export function parseChangeOrderNumberFromSheetNumbers(sheetCompanyProjectNumber, parentCompanyProjectNumber) {
  const sheet = normalizeCompanyProjectNumberToken(sheetCompanyProjectNumber)
  const parent = normalizeCompanyProjectNumberToken(parentCompanyProjectNumber)
  if (!sheet || !parent) return null
  if (sheet === parent) return null

  if (sheet.startsWith(`${parent}-`)) {
    const rest = sheet.slice(parent.length + 1).trim()
    const n = parseInt(rest, 10)
    if (Number.isFinite(n) && n >= 1 && n <= 1000) return n
    return null
  }

  if (sheet.startsWith(parent)) {
    const rest = sheet.slice(parent.length).trim()
    if (!rest || !/^\d+$/.test(rest)) return null
    const n = parseInt(rest, 10)
    if (Number.isFinite(n) && n >= 1 && n <= 1000) return n
  }

  return null
}

/**
 * CO # from the sheet’s own companyProjectNumber: last `-` segment if it is numeric (1–1000).
 * Does not require the sheet to match the open project (e.g. 7877-13 → 13).
 */
export function parseChangeOrderNumberFromSheetCpnStandalone(sheetCpnRaw) {
  const sheet = normalizeCompanyProjectNumberToken(sheetCpnRaw)
  if (!sheet) return null
  const hyphenAt = sheet.lastIndexOf('-')
  if (hyphenAt <= 0) return null
  const tail = sheet.slice(hyphenAt + 1).trim()
  if (!/^\d+$/.test(tail)) return null
  const n = parseInt(tail, 10)
  if (Number.isFinite(n) && n >= 1 && n <= 1000) return n
  return null
}

// True when the sheet project # is this job or a CO suffix of it (BASE, BASE-02, BASE02).
export function sheetCompanyProjectNumberRelatesToParent(sheetCpnRaw, parentCompanyProjectNumber) {
  const sheet = normalizeCompanyProjectNumberToken(sheetCpnRaw)
  const parent = normalizeCompanyProjectNumberToken(parentCompanyProjectNumber)
  if (!parent) return true
  if (!sheet) return true
  if (sheet === parent) return true
  if (parseChangeOrderNumberFromSheetNumbers(sheetCpnRaw, parentCompanyProjectNumber) != null) return true
  return false
}

/**
 * CO # from project name: always the segment after the *last* hyphen if that segment is numeric
 * (e.g. `… - sd - 01` → 1, `… - 02` → 2). Normalizes fancy dashes like CPN.
 */
export function parseChangeOrderNumberFromProjectName(nameRaw) {
  const name = normalizeCompanyProjectNumberToken(String(nameRaw ?? '').trim())
  if (!name) return null
  const hyphenAt = name.lastIndexOf('-')
  if (hyphenAt < 0) return null
  const tail = name.slice(hyphenAt + 1).trim()
  const m = tail.match(/^0*(\d{1,4})$/i)
  if (!m) return null
  const n = parseInt(m[1], 10)
  if (Number.isFinite(n) && n >= 1 && n <= 1000) return n
  return null
}

function excelValueToDateInputString(value) {
  const normalized = normalizeCell(value)
  if (normalized === '' || normalized == null) return null
  if (normalized instanceof Date && !isNaN(normalized.getTime())) {
    return normalized.toISOString().slice(0, 10)
  }
  const s = String(normalized).trim()
  if (!s) return null
  const d = new Date(s)
  if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10)
  return null
}

function parseProjectSheetRow(workbook, parentCompanyProjectNumber, errors) {
  const worksheet = workbook.getWorksheet(DLSTRACKER_PROJECT_TAB)
  if (!worksheet) {
    errors.push(`Worksheet "${DLSTRACKER_PROJECT_TAB}" not found.`)
    return { changeOrderNumber: null, poReceivedDate: null, sheetCompanyProjectNumber: '' }
  }

  const rowArrays = worksheetToRowArrays(worksheet)
  if (rowArrays.length < 2) {
    errors.push('Project sheet has no data row.')
    return { changeOrderNumber: null, poReceivedDate: null, sheetCompanyProjectNumber: '' }
  }

  const headers = deduplicateHeaders(
    (rowArrays[0] || []).map((h) => (h == null ? '' : String(h)))
  )
  const row = rowArrays[1]
  const idx = (name) => headers.indexOf(name)

  const cpnIdx = idx('companyProjectNumber')
  if (cpnIdx === -1) {
    errors.push('Project sheet missing companyProjectNumber column.')
    return { changeOrderNumber: null, poReceivedDate: null, sheetCompanyProjectNumber: '' }
  }

  const sheetCpnRaw = String(normalizeCell(row[cpnIdx]) ?? '').trim()
  const sheetCpnNorm = normalizeCompanyProjectNumberToken(sheetCpnRaw)
  const parentNorm = normalizeCompanyProjectNumberToken(parentCompanyProjectNumber)

  let changeOrderNumber = null

  const coIdx = idx('changeOrderNumber')
  if (coIdx !== -1) {
    const rawCo = row[coIdx]
    let n = toNumber(rawCo)
    if (n == null && rawCo !== '' && rawCo != null) {
      const p = parseInt(String(normalizeCell(rawCo)).trim(), 10)
      if (!isNaN(p)) n = p
    }
    if (n != null && Number.isFinite(n)) {
      const intCo = Math.round(Number(n))
      if (intCo >= 1 && intCo <= 1000) changeOrderNumber = intCo
    }
  }

  if (changeOrderNumber == null) {
    changeOrderNumber = parseChangeOrderNumberFromSheetCpnStandalone(sheetCpnRaw)
  }

  if (changeOrderNumber == null && parentNorm) {
    changeOrderNumber = parseChangeOrderNumberFromSheetNumbers(sheetCpnRaw, parentCompanyProjectNumber)
  }

  if (changeOrderNumber == null) {
    const nameIdx = idx('name')
    if (nameIdx !== -1) {
      changeOrderNumber = parseChangeOrderNumberFromProjectName(normalizeCell(row[nameIdx]))
    }
  }

  if (!parentNorm && changeOrderNumber == null) {
    errors.push('Parent project number is missing; cannot read change order # from the spreadsheet.')
  }

  if (changeOrderNumber == null && sheetCpnNorm !== parentNorm) {
    if (!sheetCpnRaw) {
      errors.push('Project sheet companyProjectNumber is empty.')
    }
    // Non-empty CPN that is not this job (or not a parseable CO suffix) — no error here;
    // ChangeOrder.vue shows the project-number mismatch overlay instead of a long parser message.
  }

  let poReceivedDate = null
  const poIdx = idx('poReceivedDate')
  if (poIdx !== -1) {
    poReceivedDate = excelValueToDateInputString(row[poIdx])
  }

  return { changeOrderNumber, poReceivedDate, sheetCompanyProjectNumber: sheetCpnRaw }
}

/**
 * Static field rules aligned with NewProjectImport `requiredData` (services tab)
 * and Project `services` sub-schema — no API fetch.
 */
const SERVICES_FIELDS = {
  partNumber: {
    required: true,
    type: 'string',
    minLength: 3,
    maxLength: 30
  },
  description: {
    required: true,
    type: 'string',
    minLength: 5,
    maxLength: 100
  },
  revenue: {
    required: true,
    type: 'number',
    /** Allow negatives for credits / scope removals (same magnitude cap as positive). */
    min: -10000000000,
    max: 10000000000
  },
  hours: {
    required: false,
    type: 'number',
    min: -1000000,
    max: 1000000
  },
  displayOrder: {
    required: false,
    type: 'number',
    min: 0,
    max: 1000000
  }
}

const FIELD_NAMES = Object.keys(SERVICES_FIELDS)

function deduplicateHeaders(headers) {
  const headerCount = {}
  return headers.map((header) => {
    if (header) {
      header = String(header).trim()
    }
    if (!headerCount[header]) {
      headerCount[header] = 1
    } else {
      headerCount[header] += 1
      header = `${header}_${headerCount[header] - 1}`
    }
    return header
  })
}

function normalizeCell(cell) {
  if (cell === null || cell === undefined) return ''
  if (typeof cell === 'object' && cell.richText) {
    return cell.richText.map((r) => r.text).join('')
  }
  if (typeof cell === 'object' && cell.result !== undefined) return String(cell.result)
  if (typeof cell === 'object' && cell.text) return String(cell.text)
  if (typeof cell === 'object') return cell instanceof Date ? cell.toISOString() : String(cell)
  return cell
}

function toNumber(val) {
  if (val == null || val === '') return null
  if (typeof val === 'number' && !isNaN(val)) return val
  if (typeof val === 'object' && (typeof val.result === 'number' || typeof val.value === 'number')) {
    return Number(val.result ?? val.value)
  }
  let s = String(val).replace(/,/g, '').trim()
  // Excel accounting: (500) → -500
  if (/^\(.*\)$/.test(s)) {
    s = `-${s.slice(1, -1).replace(/[^\d.+-]/g, '')}`
  }
  const n = parseFloat(s)
  return isNaN(n) ? null : n
}

function worksheetToRowArrays(worksheet) {
  const rowArrays = []
  worksheet.eachRow({ includeEmpty: false }, (row) => {
    const cells = (row.values || []).slice(1).map(normalizeCell)
    if (cells.some((cell) => cell !== null && cell !== '')) {
      rowArrays.push(cells)
    }
  })
  return rowArrays
}

function mapServiceRow(headers, row, rowIndex, errors) {
  const obj = {}
  const headerIndex = (name) => headers.indexOf(name)

  for (const field of FIELD_NAMES) {
    const def = SERVICES_FIELDS[field]
    const idx = headerIndex(field)
    if (idx === -1) {
      if (def.required) {
        errors.push(`services: Row ${rowIndex + 1}: Missing column "${field}".`)
      }
      continue
    }

    let raw = row[idx]
    let value = raw

    if (def.type === 'number') {
      value = toNumber(raw)
      if (def.required && (value == null || isNaN(value))) {
        errors.push(`services: Row ${rowIndex + 1}, "${field}": Required number is missing or invalid.`)
        continue
      }
      if (value != null && !isNaN(value)) {
        // displayOrder: tiny negatives from Excel noise only → 0
        if (field === 'displayOrder' && value < 0 && value > -1e-6) {
          value = 0
        }
        if (def.min != null && value < def.min) {
          errors.push(`services: Row ${rowIndex + 1}, "${field}": Below minimum (${def.min}).`)
        }
        if (def.max != null && value > def.max) {
          errors.push(`services: Row ${rowIndex + 1}, "${field}": Above maximum (${def.max}).`)
        }
      }
    } else {
      if (raw === null || raw === undefined) raw = ''
      value = typeof raw === 'number' || typeof raw === 'object' ? String(raw) : String(raw).trim()
      if (def.required && !value) {
        errors.push(`services: Row ${rowIndex + 1}, "${field}": Required field is missing.`)
        continue
      }
      if (value && def.minLength != null && value.length < def.minLength) {
        if (def.skipIfInvalid) {
          value = ''
        } else {
          errors.push(
            `services: Row ${rowIndex + 1}, "${field}": Minimum length is ${def.minLength}.`
          )
        }
      }
      if (value && def.maxLength != null && value.length > def.maxLength) {
        value = value.substring(0, def.maxLength)
      }
    }

    if (value !== '' && value != null) {
      obj[field] = value
    }
  }

  if (obj.partNumber) {
    obj.partNumber = String(obj.partNumber).toUpperCase()
  }

  return obj
}

function parseServicesFromLoadedWorkbook(workbook, maxRows, errors) {
  const worksheet = workbook.getWorksheet(DLSTRACKER_SERVICES_TAB)
  if (!worksheet) {
    errors.push(`Worksheet "${DLSTRACKER_SERVICES_TAB}" not found.`)
    return []
  }

  const rowArrays = worksheetToRowArrays(worksheet)
  if (rowArrays.length === 0) {
    errors.push('Services sheet is empty.')
    return []
  }

  const rawHeaders = rowArrays[0] || []
  const headers = deduplicateHeaders(rawHeaders.map((h) => (h == null ? '' : String(h))))

  const requiredCols = FIELD_NAMES.filter((f) => SERVICES_FIELDS[f].required)
  const missingHeadings = requiredCols.filter((h) => !headers.includes(h))
  if (missingHeadings.length > 0) {
    errors.push(`Missing required columns: ${missingHeadings.join(', ')}`)
    return []
  }

  const dataRows = rowArrays.slice(1, 1 + maxRows)
  const services = []

  dataRows.forEach((row, i) => {
    const nonEmpty = row.some(
      (cell) => cell !== null && cell !== '' && String(cell).trim() !== ''
    )
    if (!nonEmpty) return

    const obj = mapServiceRow(headers, row, i + 1, errors)
    const hasCore =
      obj.partNumber &&
      obj.description != null &&
      obj.description !== '' &&
      obj.revenue != null &&
      !isNaN(obj.revenue)
    if (hasCore) {
      if (obj.displayOrder == null) obj.displayOrder = 1
      if (obj.hours == null) obj.hours = 0
      obj.revenue = Math.round(Number(obj.revenue) * 100) / 100
      obj.hours = Math.round(Number(obj.hours) * 100) / 100
      services.push(obj)
    }
  })

  if (services.length === 0 && errors.length === 0) {
    errors.push('No valid service rows found.')
  }

  return services
}

/**
 * Parse the same estimate workbook as new-project import: project row (CO #, optional PO date) + services.
 * Does not call the API. Does not mutate the parent project — only supplies data for the change-order form.
 */
export async function parseChangeOrderEstimateWorkbook(arrayBuffer, options = {}) {
  const maxRows = options.maxRows ?? 1250
  const errors = []

  const workbook = new ExcelJS.Workbook()
  await workbook.xlsx.load(arrayBuffer)

  const { changeOrderNumber, poReceivedDate, sheetCompanyProjectNumber } = parseProjectSheetRow(
    workbook,
    options.parentCompanyProjectNumber,
    errors
  )

  const services = parseServicesFromLoadedWorkbook(workbook, maxRows, errors)

  return { services, changeOrderNumber, poReceivedDate, sheetCompanyProjectNumber, errors }
}
